import React, { useMemo } from 'react';
import { View, Text, TouchableOpacity, ScrollView, StyleSheet } from 'react-native';
import dayjs from 'dayjs';
import type { CalendarEvent } from './WeekTimelineView';
import EventCard from './EventCard';

interface WeekAgendaViewProps {
  events: CalendarEvent[];
  selectedDate: Date;
  weekStartDay?: number;
  numberOfDays?: number;
  onPressEvent?: (event: CalendarEvent) => void;
  onPressDay?: (date: Date) => void;
}

const PRIMARY = '#2e96e1';

const WeekAgendaView: React.FC<WeekAgendaViewProps> = ({
  events,
  selectedDate,
  weekStartDay = 0,
  numberOfDays = 7,
  onPressEvent,
  onPressDay,
}) => {
  const today = dayjs().format('YYYY-MM-DD');

  const days = useMemo(() => {
    const r: dayjs.Dayjs[] = [];
    const current = dayjs(selectedDate);
    let daysBack = current.day() - weekStartDay;
    if (daysBack < 0) daysBack += 7;
    const start = current.subtract(daysBack, 'day');
    for (let i = 0; i < numberOfDays; i++) r.push(start.add(i, 'day'));
    return r;
  }, [selectedDate, numberOfDays, weekStartDay]);

  const eventsByDay = useMemo(() => {
    const map = new Map<string, CalendarEvent[]>();
    for (const day of days) {
      const key = day.format('YYYY-MM-DD');
      const dayEvents = events
        .filter(e => dayjs(e.start).format('YYYY-MM-DD') === key)
        .sort((a, b) => a.start.getTime() - b.start.getTime());
      map.set(key, dayEvents);
    }
    return map;
  }, [events, days]);

  const hasAny = useMemo(
    () => [...eventsByDay.values()].some(arr => arr.length > 0),
    [eventsByDay],
  );

  return (
    <ScrollView style={styles.container} contentContainerStyle={styles.contentContainer} showsVerticalScrollIndicator={false}>
      {!hasAny && (
        <View style={styles.emptyState}>
          <Text style={styles.emptyIcon}>📅</Text>
          <Text style={styles.emptyTitle}>No reservations this week</Text>
          <Text style={styles.emptySubtitle}>Tap a day to add one</Text>
        </View>
      )}

      {days.map(day => {
        const key = day.format('YYYY-MM-DD');
        const isToday = key === today;
        const dayEvents = eventsByDay.get(key) || [];

        return (
          <View key={key} style={styles.daySection}>
            <TouchableOpacity
              style={styles.dayHeader}
              onPress={() => onPressDay?.(day.hour(9).minute(0).second(0).toDate())}
              activeOpacity={0.7}
            >
              <View style={styles.dayHeaderLeft}>
                <View style={[styles.dayCircle, isToday && styles.dayCircleToday]}>
                  <Text style={[styles.dayNumber, isToday && styles.dayNumberToday]}>
                    {day.format('D')}
                  </Text>
                </View>
                <View style={{ marginLeft: 12 }}>
                  <Text style={[styles.dayName, isToday && styles.dayNameToday]}>
                    {day.format('dddd')}
                  </Text>
                  <Text style={styles.dayDate}>{day.format('MMM D, YYYY')}</Text>
                </View>
              </View>
              {dayEvents.length > 0 && (
                <View style={styles.countBadge}>
                  <Text style={styles.countBadgeText}>{dayEvents.length}</Text>
                </View>
              )}
            </TouchableOpacity>

            {dayEvents.length === 0 ? (
              <View style={styles.emptyDay}>
                <Text style={styles.emptyDayText}>No reservations</Text>
              </View>
            ) : (
              <View style={styles.eventsList}>
                {dayEvents.map(event => (
                  <EventCard
                    key={event.id}
                    event={event}
                    density="full"
                    onPress={() => onPressEvent?.(event)}
                    style={styles.eventCardSpacing}
                  />
                ))}
              </View>
            )}
          </View>
        );
      })}
    </ScrollView>
  );
};

const styles = StyleSheet.create({
  container: { flex: 1, backgroundColor: '#F6F7FB' },
  contentContainer: { paddingBottom: 24 },
  emptyState: { alignItems: 'center', paddingVertical: 48 },
  emptyIcon: { fontSize: 40, marginBottom: 12 },
  emptyTitle: { fontSize: 16, fontWeight: '500', color: '#555', marginBottom: 4 },
  emptySubtitle: { fontSize: 13, color: '#999' },
  daySection: {
    marginTop: 12,
    marginHorizontal: 16,
    borderRadius: 12,
    backgroundColor: '#fff',
    overflow: 'hidden',
    borderWidth: 1,
    borderColor: '#EBEBEB',
  },
  dayHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: 14,
    paddingVertical: 12,
    borderBottomWidth: 1,
    borderBottomColor: '#F0F0F0',
  },
  dayHeaderLeft: { flexDirection: 'row', alignItems: 'center' },
  dayCircle: {
    width: 38, height: 38, borderRadius: 19,
    backgroundColor: '#F0F0F0',
    alignItems: 'center', justifyContent: 'center',
  },
  dayCircleToday: { backgroundColor: PRIMARY },
  dayNumber: { fontSize: 17, fontWeight: '500', color: '#111' },
  dayNumberToday: { color: '#fff' },
  dayName: { fontSize: 14, fontWeight: '500', color: '#111', lineHeight: 18 },
  dayNameToday: { color: PRIMARY },
  dayDate: { fontSize: 11, color: '#999', lineHeight: 15 },
  countBadge: {
    backgroundColor: PRIMARY,
    borderRadius: 10,
    minWidth: 20,
    height: 20,
    paddingHorizontal: 6,
    alignItems: 'center',
    justifyContent: 'center',
  },
  countBadgeText: { color: '#fff', fontSize: 11, fontWeight: '500' },
  emptyDay: { paddingVertical: 14, paddingHorizontal: 16 },
  emptyDayText: { fontSize: 13, color: '#BBB' },
  eventsList: { paddingVertical: 4, paddingHorizontal: 10 },
  eventCardSpacing: { marginVertical: 5 },
});

export default WeekAgendaView;
